import React from 'react';
import { FiPhone, FiClock, FiFileText, FiCheckSquare, FiChevronLeft } from 'react-icons/fi';
import { useDialerCallDetail } from '../../context/DialerCallDetailContext';
import { formatDateTime, formatDurationSeconds, statusMeta, type CallHistoryItem } from './DialerHistory';
import { useCallConclusion } from './useCallConclusion';
import CallConclusionFields from './CallConclusionFields';
import '../../styles/Voice.css';
import '../../styles/Dialer.css';

function parseCallNotes(raw?: string): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((n): n is string => typeof n === 'string' && n.trim() !== '');
  } catch {
    // Not JSON — treat the raw string itself as a single note.
  }
  return raw.trim() ? [raw.trim()] : [];
}

function statusBadgeColors(tone: 'consumed' | 'partial' | 'not-consumed') {
  if (tone === 'consumed') return { background: 'rgba(52,211,153,0.1)', color: 'var(--accent-green)' };
  if (tone === 'not-consumed') return { background: 'rgba(239,68,68,0.1)', color: 'var(--accent-red)' };
  return { background: 'rgba(139,92,246,0.1)', color: 'var(--accent-purple-light)' };
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="call-detail__field">
    <span className="call-detail__field-label">{label}</span>
    <span className="call-detail__field-value">{children}</span>
  </div>
);

interface DialerCallDetailPanelBodyProps {
  item: CallHistoryItem;
  onClose: () => void;
  onSaved: (result: { callNotes: string; insuredId: string; contactName: string }) => void;
}

// Keyed by item.id from the parent so switching between history rows fully
// remounts this body — otherwise the conclusion form's local state (notes,
// insured, task fields) would leak from one call's row into the next.
const DialerCallDetailPanelBody: React.FC<DialerCallDetailPanelBodyProps> = ({ item, onClose, onSaved }) => {
  const meta = statusMeta(item.callStatus);

  const conclusion = useCallConclusion({
    parentCallSid: item.parentCallSid,
    initialInsuredId: item.insuredId || '',
    initialContactName: item.insuredName || '',
    initialCallNotes: parseCallNotes(item.callNotes).join('\n'),
    transcript: item.transcriptText || '',
    summary: item.summary || '',
    phoneNumber: item.toNumber,
    onSaved,
  });

  return (
    <div className="call-detail-panel">
      <div className="call-detail-panel__header">
        <div className="call-detail__header-row">
          <div
            className="call-detail__icon-box"
            style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)', color: 'var(--accent-purple-light)' }}
          >
            <FiPhone size={16} />
          </div>
          <div>
            <div className="call-detail-panel__title">+{item.toNumber || 'Unknown number'}</div>
            <div className="call-detail__meta-row">
              <span className="call-detail__status-badge" style={statusBadgeColors(meta.tone)}>{meta.label}</span>
              <span className="call-detail__meta-timestamp">{formatDateTime(item.startedOn)}</span>
            </div>
          </div>
        </div>
        <button className="call-detail-panel__close" title="Back" onClick={onClose}>
          <FiChevronLeft size={16} />
        </button>
      </div>

      <div className="call-detail-panel__body">
        {/* Stats row */}
        <div className="call-detail__stats-row">
          <div className="call-detail__stat-cell">
            <div className="call-detail__stat-value">{formatDurationSeconds(item.duration)}</div>
            <div className="call-detail__stat-label call-detail__stat-label--icon"><FiClock size={10} /> Duration</div>
          </div>
          <div className="call-detail__stat-cell call-detail__stat-cell--bordered">
            <div className="call-detail__stat-value">{formatDateTime(item.startedOn)}</div>
            <div className="call-detail__stat-label">Started</div>
          </div>
          <div className="call-detail__stat-cell">
            <div className="call-detail__stat-value">{item.endedOn ? formatDateTime(item.endedOn) : '—'}</div>
            <div className="call-detail__stat-label">Ended</div>
          </div>
        </div>

        {/* Number fields */}
        <div className="call-detail__fields-grid">
          <Field label="From">{item.fromNumber ? `+${item.fromNumber.replace(/^\+/, '')}` : '—'}</Field>
          <Field label="To">{item.toNumber ? `+${item.toNumber.replace(/^\+/, '')}` : '—'}</Field>
        </div>

        {/* Recording */}
        {/* Summary */}
        <div>
          <div className="call-detail__section-label"><FiCheckSquare size={12} /> Summary</div>
          <div className="call-detail__summary-box">{item.summary || 'Not generated for this call.'}</div>
        </div>

        {/* Insured attach + editable notes + AMS/Command Center task creation —
            same conclusion save flow as the live post-call summary. */}
        <CallConclusionFields conclusion={conclusion} saveLabel="Save" />

        {/* Transcript */}
        <div>
          <div className="call-detail__section-label"><FiFileText size={12} /> Transcript</div>
          <div className="call-detail__transcript-box">{item.transcriptText || 'No transcript available for this call.'}</div>
        </div>

        {/* Call IDs — everything else available on the record */}
        <div>
          <div className="call-detail__section-label">Call IDs</div>
          <div className="call-detail__fields-grid">
            <Field label="Call Sid">{item.parentCallSid || '—'}</Field>
            <Field label="Conference Sid">{item.conferenceSid || '—'}</Field>
            <Field label="Recording Sid">{item.recordingSid || '—'}</Field>
          </div>
        </div>
      </div>
    </div>
  );
};

const DialerCallDetailSidebarPanel: React.FC = () => {
  const { dialerCallDetail, setDialerCallDetail } = useDialerCallDetail();
  if (!dialerCallDetail) return null;

  return (
    <DialerCallDetailPanelBody
      key={dialerCallDetail.id}
      item={dialerCallDetail}
      onClose={() => setDialerCallDetail(null)}
      onSaved={(result) => setDialerCallDetail((prev) => (prev ? {
        ...prev,
        callNotes: result.callNotes,
        insuredId: result.insuredId,
        insuredName: result.contactName,
      } : prev))}
    />
  );
};

export default DialerCallDetailSidebarPanel;
